import { useContext, useCallback } from 'react';
import { GlobalErrorContext, extractErrorDetails, ErrorDetails } from '../context/GlobalErrorContext';
import { captureMessage } from 'monitoring';

export const useGlobalError = () => {
    const errorSubject$ = useContext(GlobalErrorContext);

    const handleError = useCallback((error: any, options?: { 
        capture?: boolean;
        context?: string;
        userMessage?: string;
    }) => {
        const errorDetails = extractErrorDetails(error);
        
        // Log to monitoring in development or if explicitly requested
        if (options?.capture !== false && (process.env.NODE_ENV === 'development' || options?.capture)) {
            const contextMsg = options?.context ? `[${options.context}] ` : '';
            captureMessage(`${contextMsg}${errorDetails.message}`);
        }

        // Emit to global error subject
        if (errorSubject$) {
            errorSubject$.next(error instanceof Error ? error : new Error(errorDetails.message));
        }

        return errorDetails;
    }, [errorSubject$]);

    const handleAsyncError = useCallback(
        async <T,>(
            asyncFn: () => Promise<T>,
            options?: { 
                capture?: boolean;
                context?: string;
                userMessage?: string;
                rethrow?: boolean;
            }
        ): Promise<T | null> => {
            try {
                return await asyncFn();
            } catch (error) {
                handleError(error, options);
                if (options?.rethrow) {
                    throw error;
                }
                return null;
            }
        },
        [handleError]
    );

    const createUserFriendlyMessage = useCallback((error: ErrorDetails): string => {
        // You can customize this based on your application's needs
        if (error.networkError) {
            return 'Network error occurred. Please check your connection and try again.';
        }
        
        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
            // Extract user-friendly messages from GraphQL errors
            const messages = error.graphQLErrors
                .map((err: any) => err.message || err.extensions?.message)
                .filter(Boolean);
            
            if (messages.length > 0) {
                return messages[0]; // Return first GraphQL error message
            }
        }

        // Return the original message if it's not too technical
        if (error.message && !error.message.includes('Error:')) {
            return error.message;
        }

        return 'An unexpected error occurred. Please try again.';
    }, []);

    return {
        handleError,
        handleAsyncError,
        createUserFriendlyMessage,
        extractErrorDetails,
        errorSubject$
    };
};
